import { useEffect, useMemo, useRef, useState, UIEvent } from 'react';
import { Input, Radio, Spin, Button, Empty } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

export interface ServerFilterOption {
  value: string;
  label: string;
}

interface LoadResult {
  items: ServerFilterOption[];
  totalCount: number;
}

interface ServerFilterDropdownProps {
  /** Valor atualmente selecionado (single-select). */
  selectedValue?: string;
  /** Dispara o filtro server-side. value === undefined limpa. */
  onChange: (value: string | undefined) => void;
  /** Fecha o dropdown da coluna (injetado pelo antd via filterDropdownProps). */
  close?: () => void;

  /**
   * Modo "cadastro": lista completa já em memória; busca é client-side.
   * Use para campos bounded (tipo de inspeção, tipo de imóvel, motivo, prestadora).
   */
  staticOptions?: ServerFilterOption[];

  /**
   * Modo "texto livre": carrega opções paginadas do servidor com scroll infinito.
   * Use para campos unbounded (cidade, canal).
   */
  loadOptions?: (params: { search: string; pageNumber: number; pageSize: number }) => Promise<LoadResult>;

  pageSize?: number;
}

const PAGE_SIZE = 20;

/**
 * Dropdown de filtro de coluna server-side. Substitui o `filters`+`onFilter`
 * nativo do antd (que só enxerga a página atual). As opções são globais:
 * vêm de um cadastro completo (staticOptions) ou de um endpoint distinct
 * paginado com busca + scroll infinito (loadOptions).
 */
export function ServerFilterDropdown({
  selectedValue,
  onChange,
  close,
  staticOptions,
  loadOptions,
  pageSize = PAGE_SIZE,
}: ServerFilterDropdownProps) {
  const [search, setSearch] = useState('');
  const [asyncOptions, setAsyncOptions] = useState<ServerFilterOption[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  // Token para descartar respostas fora de ordem (busca/scroll concorrentes).
  const requestSeq = useRef(0);

  const isAsync = typeof loadOptions === 'function';

  // ---- Modo cadastro: filtra client-side sobre a lista completa ----
  const staticFiltered = useMemo(() => {
    if (!staticOptions) return [];
    const term = search.trim().toLowerCase();
    if (!term) return staticOptions;
    return staticOptions.filter((o) => o.label.toLowerCase().includes(term));
  }, [staticOptions, search]);

  // ---- Modo texto livre: carrega do servidor (com debounce na busca) ----
  const fetchPage = async (pageNumber: number, searchTerm: string, append: boolean) => {
    if (!loadOptions) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await loadOptions({ search: searchTerm, pageNumber, pageSize });
      if (seq !== requestSeq.current) return; // resposta obsoleta
      setTotal(res.totalCount);
      setAsyncOptions((prev) => (append ? [...prev, ...res.items] : res.items));
      setPage(pageNumber);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAsync) return;
    const handle = setTimeout(() => {
      fetchPage(1, search, false);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, isAsync]);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    if (!isAsync || loading) return;
    const el = e.currentTarget;
    const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (reachedBottom && asyncOptions.length < total) {
      fetchPage(page + 1, search, true);
    }
  };

  const options = isAsync ? asyncOptions : staticFiltered;

  const apply = (value: string | undefined) => {
    onChange(value);
    close?.();
  };

  return (
    <div style={{ padding: 8, width: 240 }} onKeyDown={(e) => e.stopPropagation()}>
      <Input
        autoFocus
        size="small"
        allowClear
        placeholder="Buscar..."
        prefix={<SearchOutlined />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />

      <div
        ref={listRef}
        onScroll={handleScroll}
        style={{ maxHeight: 240, overflowY: 'auto' }}
      >
        {options.length === 0 && !loading ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sem opções" style={{ margin: '12px 0' }} />
        ) : (
          <Radio.Group
            value={selectedValue}
            onChange={(e) => apply(e.target.value)}
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            {options.map((o) => (
              <Radio key={o.value} value={o.value} style={{ whiteSpace: 'normal' }}>
                {o.label}
              </Radio>
            ))}
          </Radio.Group>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: 8 }}>
            <Spin size="small" />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
        <Button
          type="link"
          size="small"
          disabled={!selectedValue}
          onClick={() => apply(undefined)}
          style={{ paddingLeft: 0 }}
        >
          Limpar
        </Button>
        <Button type="primary" size="small" onClick={() => close?.()}>
          OK
        </Button>
      </div>
    </div>
  );
}
